import tripApi from '@/api/tripApi';
import NavigationBar from '@/components/navigationBar';
import { PageTitle } from '@/components/pageTitle';
import { useHandleError } from '@/lib/errors';
import createValidateSearch from '@/lib/searchValidation';
import { TripEditorChooseTemplate } from '@/pages/tripEditor/tripEditorChooseTemplate';
import TripEditorData from '@/pages/tripEditor/tripEditorData';
import { TripEditorForm } from '@/pages/tripEditor/tripEditorForm';
import { Breadcrumbs, Button, Container, Skeleton, Title } from '@mantine/core';
import { IconHome } from '@tabler/icons-react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { omit, range } from 'lodash';
import { useTranslator } from 'translations';
import { z } from 'zod';

export const Route = createFileRoute('/_auth/_home/t/')({
    component: RouteComponent,

    validateSearch: createValidateSearch(
        z.object({
            template: z.string().optional(),
        }),
    ),

    loaderDeps({ search: { template } }) {
        return { template };
    },

    async loader({ deps: { template } }) {
        if (template) {
            await tripApi.get(template).get();
        }
    },
});

function RouteComponent() {
    const t = useTranslator();
    const navigate = useNavigate();
    const { template: templateId } = Route.useSearch();
    const [template, error] = tripApi.get(templateId ?? '').useCache({ disabled: !templateId });
    useHandleError(error);

    return (
        <PageTitle title={t('header.newTrip')}>
            <NavigationBar
                breadcrumbs={
                    <Breadcrumbs>
                        <Link to="/">
                            <IconHome />
                        </Link>
                        <Link to="/t">{t('header.newTrip')}</Link>
                    </Breadcrumbs>
                }
            />

            <Container
                size="md"
                css={{
                    width: '100%',
                    marginTop: 'var(--mantine-spacing-xl)',
                    flex: 1,
                    display: 'grid',
                    alignContent: 'start',
                    rowGap: 'var(--mantine-spacing-md)',
                    padding: '0 var(--mantine-spacing-sm)',
                }}
            >
                <Title
                    order={1}
                    css={{
                        marginBottom: 'var(--mantine-spacing-md)',
                    }}
                >
                    {t('tripEditor.empty')}
                </Title>

                {templateId && !template ? (
                    range(5).map((i) => <Skeleton key={i} h={40} w="100%" />)
                ) : (
                    <TripEditorForm.Form
                        validations={{}}
                        original={template && omit(template, 'id')}
                        onSubmit={async (_e, form) => {
                            const trip = await tripApi.create(form.draft.tripData);
                            navigate({ to: '/t/$tripId', params: { tripId: trip.id } });
                        }}
                        css={{
                            display: 'grid',
                            gap: 'var(--mantine-spacing-md)',
                        }}
                    >
                        <TripEditorData />

                        <TripEditorForm.FormState
                            selector={(form) => [form.isValid, form.saveInProgress] as const}
                        >
                            {([isValid, saveInProgress]) => (
                                <Button
                                    type="submit"
                                    variant="filled"
                                    color={isValid ? 'secondary' : 'gray'}
                                    loading={saveInProgress}
                                    css={{
                                        justifySelf: 'center',
                                    }}
                                >
                                    {t('tripEditor.create')}
                                </Button>
                            )}
                        </TripEditorForm.FormState>
                    </TripEditorForm.Form>
                )}

                <TripEditorChooseTemplate css={{ marginTop: 'var(--mantine-spacing-xl)' }} />
            </Container>
        </PageTitle>
    );
}
